import { useTranslation } from "react-i18next";
import { cx } from "../cx";
import { RAIL_TEMPLATE } from "../data/RailTable";

type Variant = "text" | "row" | "table";

/**
 * Loading placeholder. `text` is a paragraph of lines, `row` one list row,
 * `table` a header plus rows on the given grid template (rail template by default).
 */
export function Skeleton({
  variant = "text",
  lines = 3,
  rows = 5,
  template = RAIL_TEMPLATE,
  className = "",
}: {
  variant?: Variant;
  lines?: number;
  rows?: number;
  template?: string;
  className?: string;
}) {
  const { t } = useTranslation("components");
  const bar = "h-2.5 rounded-sm bg-line-soft";
  const lineIds = Array.from({ length: lines }, (_, i) => `line-${i}`);
  const rowIds = Array.from({ length: rows + 1 }, (_, i) => `row-${i}`);
  const columnIds = template.split(/\s+/).map((_, i) => `col-${i}`);
  return (
    <div
      role="status"
      aria-label={t("skeleton.loading")}
      aria-busy="true"
      data-component="Skeleton"
      data-variant={variant}
      className={cx("animate-pulse", className)}
    >
      {variant === "text" ? (
        <div className="flex flex-col gap-2">
          {lineIds.map((id, i) => (
            <div key={id} className={bar} style={{ width: i === lines - 1 ? "60%" : "100%" }} />
          ))}
        </div>
      ) : null}
      {variant === "row" ? (
        <div className="flex items-center gap-3 py-2">
          <div className={cx(bar, "w-6")} />
          <div className={cx(bar, "flex-1")} />
          <div className={cx(bar, "w-16")} />
        </div>
      ) : null}
      {variant === "table" ? (
        <div className="flex flex-col">
          {rowIds.map((id, r) => (
            <div
              key={id}
              className={cx(
                "grid items-center gap-3 border-b px-3.5 py-2.5",
                r === 0 ? "border-line" : "border-line-soft",
              )}
              style={{ gridTemplateColumns: template }}
            >
              {columnIds.map((cid) => (
                <div key={cid} className={cx(bar, r === 0 ? "h-2" : "")} />
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
